/**
 * Assessment reviews API client — owner-side (authenticated) helpers for the
 * assessment review cycle. The public submit path lives in the runner (vanilla).
 */

import axios from 'axios';
import { getApiBaseUrl } from './api';
import { authHeaders } from './authHeaders';
import type {
  ListAssessmentReviewsResponse,
  AssessmentOwnerReviewComment,
  ReviewCommentStatus,
} from '../components/assessment-workspace/reviews/types';

async function authedRequest<T>(method: 'get' | 'patch' | 'delete', path: string, data?: unknown): Promise<T> {
  const res = await axios.request<T>({
    method,
    url: `${getApiBaseUrl()}${path}`,
    data,
    headers: await authHeaders(),
  });
  return res.data;
}

export async function listAssessmentReviewsForConversation(conversationId: string): Promise<ListAssessmentReviewsResponse> {
  return authedRequest<ListAssessmentReviewsResponse>('get', `/conversations/${conversationId}/assessment/reviews`);
}

export async function patchAssessmentReviewComment(
  conversationId: string,
  reviewId: string,
  commentId: string,
  update: { status?: ReviewCommentStatus; creatorNote?: string | null; sentMessageId?: string | null },
): Promise<{ success: boolean; comment: AssessmentOwnerReviewComment }> {
  return authedRequest('patch', `/conversations/${conversationId}/assessment/reviews/${reviewId}/comments/${commentId}`, update);
}

export async function deleteAssessmentReviewById(conversationId: string, reviewId: string): Promise<{ success: boolean }> {
  return authedRequest('delete', `/conversations/${conversationId}/assessment/reviews/${reviewId}`);
}

/** Toggle review collection on a specific share link. */
export async function setAssessmentReviewsEnabled(
  conversationId: string,
  shareLinkId: string,
  allowReviews: boolean,
): Promise<{ success: boolean; allowReviews: boolean }> {
  return authedRequest('patch', `/conversations/${conversationId}/assessment/publish/${shareLinkId}/reviews`, { allowReviews });
}
